import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { assignConfigToUser, setUserAutoActivate } from '../../../services/apiClient';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import AssignConfigControl from './AssignConfigControl';

/**
 * One row in the tenant admin Users tab: email + remove button (as before
 * Task 7's extraction), plus a chevron toggle that expands to show this
 * user's dashboard state (last active file, active shared config,
 * auto-activate-latest-ETL-file flag) and a per-user AssignConfigControl.
 */
const UserRow = ({ user, customerId, assignableConfigs, onRemove, onAssigned, onAutoActivateChanged }) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);
  const [autoActivatePending, setAutoActivatePending] = useState(false);
  const [autoActivateErr, setAutoActivateErr] = useState(null);

  const handleAssign = (id, name) =>
    assignConfigToUser(customerId, user.user_id, id, name).then(() => onAssigned(user.user_id, id, name));

  const handleToggleAutoActivate = async (checked) => {
    setAutoActivatePending(true);
    setAutoActivateErr(null);
    try {
      await setUserAutoActivate(customerId, user.user_id, checked);
      onAutoActivateChanged(user.user_id, checked);
    } catch (e) {
      setAutoActivateErr(e.message);
    } finally {
      setAutoActivatePending(false);
    }
  };

  return (
    <li className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors">
      <div className="flex items-center justify-between px-3 py-2.5">
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <span className="text-sm text-gray-800">{user.email}</span>
        </button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-error-500 hover:text-error-600 hover:bg-error-50"
          onClick={() => onRemove(user.user_id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">{t('userStates.lastActiveFile')}:</span>{' '}
            {user.last_active_file_type || t('userStates.none')}
          </div>
          <div>
            <span className="font-medium">{t('userStates.lastSignIn')}:</span>{' '}
            {user.last_sign_in_at || t('userStates.neverSignedIn')}
          </div>
          <div>
            <span className="font-medium">{t('userStates.activeSharedConfig')}:</span>{' '}
            {user.active_shared_config_name || t('userStates.none')}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{t('userStates.autoActivateLatestEtlFile')}</span>
            <Switch
              checked={!!user.auto_activate_latest_etl_file}
              disabled={autoActivatePending}
              onCheckedChange={handleToggleAutoActivate}
            />
          </div>
          {autoActivateErr && <div className="text-xs text-error-600">{autoActivateErr}</div>}
          <AssignConfigControl
            assignableConfigs={assignableConfigs}
            onAssign={handleAssign}
            label={t('assignConfig.assign')}
          />
        </div>
      )}
    </li>
  );
};

export default UserRow;
